import React from 'react';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import ChipSelectMenu from '../../forms/ChipSelectMenu';
import SelectMenu from '../../forms/SelectMenu';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { ServicePlan } from '../../../data/models/client-program.model';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  PricingFunction,
  PricingModel,
} from '../../../data/models/pricing-model.model';
import TextInput from '../../forms/TextInput';
import {
  ClientServicePricing,
  PricingCharge,
} from '../../../data/models/client-service-pricing.model';
import { DateTime } from 'luxon';
import ChargeTypeItem from './ChargeTypeItem';

const PREFIX = 'ClientServicePricingDialog';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  pricing: `${PREFIX}-pricing`,
  dates: `${PREFIX}-dates`,
  date: `${PREFIX}-date`,
  footer: `${PREFIX}-footer`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    margin: theme.spacing(2),
  },
  [`& .${classes.left}`]: {
    minWidth: 450,
    width: 0,
  },
  [`& .${classes.right}`]: {
    flexGrow: 1,
    minWidth: 600,
    width: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.pricing}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
  [`& .${classes.dates}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  [`& .${classes.date}`]: {
    flexGrow: 1,
  },
  [`& .${classes.footer}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

interface ClientServicePricingDialogProps {
  clientServicePricing?: ClientServicePricing;
  open: boolean;
  close: () => void;
  title: string;
  clientBC: string;
  servicePlans: ServicePlan[];
  pricingModels: PricingModel[];
}

export default function ClientServicePricingDialog({
  clientServicePricing,
  open,
  close,
  title,
  clientBC,
  servicePlans,
  pricingModels,
}: ClientServicePricingDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const pricingFunctions = useAppSelector(
    accountingSlice.selectPricingFunctions,
  );

  const [selectedServicePlans, setSelectedServicePlans] = React.useState<
    string[]
  >([]);
  const [pricingModel, setPricingModel] = React.useState<PricingModel>();

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      TransportationServices: [] as string[],
      Name: '',
      Description: '',
      PricingModel: '',
      Params: [] as any[],
    },
  });

  const selectedTransportationServices = formHook.watch(
    'TransportationServices',
  );
  const selectedPricingModel = formHook.watch('PricingModel');

  React.useEffect(() => {
    if (clientServicePricing) {
      setSelectedServicePlans(clientServicePricing.servicePlanUUIDs);
      const params = clientServicePricing.pricingModel.charges.map((charge) => {
        const fees: any = {};
        charge.functionParameters.forEach((fp) => {
          fees[fp.name] = fp.value;
        });
        fees.cptCodeBC = charge.cptCodeBC;
        return fees;
      });
      formHook.reset({
        TransportationServices: clientServicePricing.transportationServiceUUIDs,
        Name: clientServicePricing.name,
        Description: clientServicePricing.description,
        PricingModel: clientServicePricing.pricingModel.pricingModelUUID,
        Params: params,
      });
    } else {
      setSelectedServicePlans([]);
      formHook.reset({
        TransportationServices: [] as string[],
        Name: '',
        Description: '',
        PricingModel: '',
        Params: [] as any[],
      });
    }
  }, [clientServicePricing]);

  React.useEffect(() => {
    if (selectedPricingModel) {
      const pm = pricingModels.find(
        (p) => p.pricingModelUUID === selectedPricingModel,
      );
      setPricingModel(pm);
    } else {
      setPricingModel(undefined);
    }
  }, [selectedPricingModel]);

  const transportServices = useAppSelector(
    accountingSlice.selectTransportationServices,
  );

  const renderDate = (params: GridCellParams): React.ReactElement => {
    let date = '';
    if (params.value) {
      date = DateTime.fromISO(params.value as string).toLocaleString(
        DateTime.DATE_SHORT,
      );
    }
    return <>{date}</>;
  };

  const servicePlanColumns: GridColDef[] = [
    {
      field: 'clientServicePlanName',
      headerName: t('client.servicePlan'),
      flex: 1,
      sortable: false,
      editable: true,
    },
    {
      field: 'effectiveStartDate',
      headerName: t('client.effectiveStart'),
      width: 140,
      sortable: false,
      editable: true,
      renderCell: renderDate,
    },
    {
      field: 'effectiveEndDate',
      headerName: t('client.effectiveEnd'),
      width: 130,
      sortable: false,
      editable: true,
      renderCell: renderDate,
    },
  ];

  const onServicePlanChange = (model: GridRowSelectionModel) => {
    setSelectedServicePlans(model as string[]);
  };

  const getPricingFunction = (
    pricingFunctionBC: string,
  ): PricingFunction | undefined => {
    return pricingFunctions.find(
      (c) => c.pricingFunctionBC === pricingFunctionBC,
    );
  };

  const createClientServicePricing = (
    servicePlanUUIDs: string[],
    transportationServiceUUIDs: string[],
    data: any,
  ): ClientServicePricing => {
    const charges: PricingCharge[] = pricingModel!.chargeTypes.map(
      (ct, idx): PricingCharge => {
        const pricingFunction = getPricingFunction(ct.pricingFunctionBC);
        const params =
          pricingFunction?.parameters.filter(
            (p) => p.type === 'UserSupplied',
          ) || [];
        const functionParameters = params.map((p) => {
          return {
            name: p.name,
            value: data.Params[idx][p.name],
          };
        });

        const charge: PricingCharge = {
          chargeTypeBC: ct.chargeTypeBC,
          pricingFunctionBC: pricingFunction!.pricingFunctionBC,
          functionParameters,
        };

        const cptCodeBC = data.Params[idx].cptCodeBC;
        if (cptCodeBC) {
          charge.cptCodeBC = cptCodeBC;
        }

        return charge;
      },
    );

    return {
      clientServicePricingUUID: clientServicePricing
        ? clientServicePricing.clientServicePricingUUID
        : undefined,
      clientBC,
      name: data.Name,
      description: data.Description,
      servicePlanUUIDs,
      transportationServiceUUIDs,
      pricingModel: {
        pricingModelUUID: selectedPricingModel,
        charges,
      },
    };
  };

  const save = async (data: any) => {
    const newClientServicePricing = createClientServicePricing(
      selectedServicePlans,
      data.TransportationServices,
      data,
    );
    try {
      if (clientServicePricing) {
        await dispatch(
          accountingSlice.updateClientServicePricingAsync(
            newClientServicePricing,
          ),
        );
      } else {
        await dispatch(
          accountingSlice.createClientServicePricingAsync(
            newClientServicePricing,
          ),
        );
      }
      formHook.reset();
      close();
    } catch (e) {
      console.log(e);
    }
  };

  const cancel = () => {
    formHook.reset();
    close();
  };

  return (
    <RootDialog fullScreen={true} open={open} onClose={cancel}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <IconButton
          onClick={cancel}
          aria-label="cancel"
          data-testid="cancel"
          size="large">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.container}>
        <DataGridPro
          className={classes.left}
          getRowId={(row) => row.servicePlanUUID}
          rows={servicePlans}
          rowSelectionModel={selectedServicePlans}
          columns={servicePlanColumns}
          checkboxSelection={true}
          disableColumnMenu={true}
          disableColumnSelector={true}
          disableRowSelectionOnClick={true}
          hideFooter={true}
          onRowSelectionModelChange={onServicePlanChange}
        />
        <Box className={classes.right}>
          <ChipSelectMenu
            label={t('client.transportationServices')}
            name="TransportationServices"
            values={transportServices}
            valueAttr="transportationServiceUUID"
            labelAttr="name"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('client.pricingModel')}
            name="PricingModel"
            values={pricingModels}
            valueAttr="pricingModelUUID"
            labelAttr="name"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          {selectedServicePlans.length > 0 &&
            selectedTransportationServices.length > 0 &&
            !!selectedPricingModel && (
              <Paper className={classes.pricing}>
                <TextInput
                  name="Name"
                  label={t('client.servicePricingName')}
                  control={formHook.control}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <TextInput
                  name="Description"
                  label={t('client.servicePricingDescription')}
                  control={formHook.control}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <Grid container spacing={2}>
                  {pricingModel?.chargeTypes.map((ct, idx) => (
                    <ChargeTypeItem
                      key={idx}
                      chargeType={ct}
                      formHook={formHook}
                      idx={idx}
                      displayInputLabels={true}
                    />
                  ))}
                </Grid>
              </Paper>
            )}
        </Box>
      </DialogContent>
      <Box className={classes.footer}>
        <Box className={classes.actions}>
          <Button variant="contained" color="secondary" onClick={cancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              selectedServicePlans?.length < 1 || !formHook.formState.isValid
            }
            onClick={formHook.handleSubmit(save)}>
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    </RootDialog>
  );
}
